import {
  type ChangeEvent,
  type ClipboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/utils/cn';

import { cleanForEditor, forScreen, imageProblem, MAIL_LOOK, readAsDataUrl } from './signatureHtml';

const FONTS = ['Arial', 'Calibri', 'Verdana', 'Tahoma', 'Georgia', 'Times New Roman'];

/** `execCommand('fontSize')` takes 1 to 7; what each one is, in Outlook's points. */
const SIZES: { value: string; label: string }[] = [
  { value: '1', label: '8' },
  { value: '2', label: '10' },
  { value: '3', label: '12' },
  { value: '4', label: '14' },
  { value: '5', label: '18' },
];

/**
 * The editor of a signature (contract v10.57): a `contentEditable` with a toolbar, so what
 * comes from Outlook (tables, fonts, colours, the logo) stays as it is. An editor with its
 * own model (TipTap, Lexical) would rewrite it (ADR 0054 of the back).
 *
 * Bold, italic, underline, font, size, colour, alignment, link and image, plus «Ver el
 * HTML» for whoever wants to paste or touch the code. Everything pasted or loaded is
 * cleaned before it is painted (`cleanForEditor`); the back cleans it again on saving.
 * An image goes inside as `data:`, the way the back stores it.
 *
 * Uncontrolled: `initial` is read when it changes (an import), and `onChange` says what
 * there is after every edit.
 */
export function SignatureEditor({
  initial,
  onChange,
  onProblem,
  toolbarExtra,
}: {
  initial: string;
  onChange: (html: string) => void;
  onProblem: (message: string | null) => void;
  toolbarExtra?: ReactNode;
}) {
  const area = useRef<HTMLDivElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const saved = useRef<Range | null>(null);
  const [source, setSource] = useState(false);
  const [code, setCode] = useState('');
  const [linking, setLinking] = useState(false);
  const [link, setLink] = useState('https://');
  const id = useId();

  useEffect(() => {
    if (area.current) area.current.innerHTML = forScreen(cleanForEditor(initial));
  }, [initial]);

  function changed(): void {
    onChange(area.current?.innerHTML ?? '');
  }

  function remember(): void {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && area.current?.contains(selection.anchorNode)) {
      saved.current = selection.getRangeAt(0);
    }
  }

  function restore(): void {
    area.current?.focus();
    const selection = window.getSelection();
    if (saved.current && selection) {
      selection.removeAllRanges();
      selection.addRange(saved.current);
    }
  }

  function run(command: string, value?: string): void {
    restore();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command, false, value);
    remember();
    changed();
  }

  function paste(event: ClipboardEvent<HTMLDivElement>): void {
    const html = event.clipboardData.getData('text/html');
    const image = Array.from(event.clipboardData.files).find((file) =>
      file.type.startsWith('image/'),
    );
    if (!html && image) {
      event.preventDefault();
      void insertImage(image);
      return;
    }
    if (!html) return; // Plain text: the browser pastes it as text.
    event.preventDefault();
    document.execCommand('insertHTML', false, cleanForEditor(html));
    changed();
  }

  async function insertImage(file: File): Promise<void> {
    const problem = imageProblem(file);
    onProblem(problem);
    if (problem) return;
    run('insertImage', await readAsDataUrl(file));
  }

  function pickImage(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void insertImage(file);
  }

  function toggleSource(): void {
    if (source) {
      const cleaned = cleanForEditor(code);
      if (area.current) area.current.innerHTML = cleaned;
      onChange(cleaned);
    } else {
      setCode(area.current?.innerHTML ?? '');
    }
    setSource(!source);
  }

  function addLink(): void {
    const target = link.trim();
    setLinking(false);
    if (!/^(https?:\/\/|mailto:|tel:)\S+$/i.test(target)) {
      onProblem('Un enlace empieza por https://, mailto: o tel:.');
      return;
    }
    onProblem(null);
    run('createLink', target);
  }

  const tool =
    'h-8 min-w-8 rounded px-2 text-[13px] text-ink-2 hover:bg-paper-2 disabled:opacity-40';

  return (
    <div className="flex flex-col gap-2">
      <div
        role="toolbar"
        aria-label="Formato de la firma"
        className="border-line bg-paper-2/60 flex flex-wrap items-center gap-1 rounded-md border p-1"
        // Clicking a tool must not take the selection away from the text.
        onMouseDown={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest('button')) event.preventDefault();
        }}
      >
        <button
          type="button"
          className={cn(tool, 'font-bold')}
          title="Negrita"
          aria-label="Negrita"
          disabled={source}
          onClick={() => run('bold')}
        >
          N
        </button>
        <button
          type="button"
          className={cn(tool, 'italic')}
          title="Cursiva"
          aria-label="Cursiva"
          disabled={source}
          onClick={() => run('italic')}
        >
          K
        </button>
        <button
          type="button"
          className={cn(tool, 'underline')}
          title="Subrayado"
          aria-label="Subrayado"
          disabled={source}
          onClick={() => run('underline')}
        >
          S
        </button>
        <select
          aria-label="Fuente"
          className="field h-8 w-auto py-0 text-[13px]"
          disabled={source}
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) run('fontName', event.target.value);
            event.target.value = '';
          }}
        >
          <option value="">Fuente</option>
          {FONTS.map((font) => (
            <option key={font} value={font} style={{ fontFamily: font }}>
              {font}
            </option>
          ))}
        </select>
        <select
          aria-label="Tamaño"
          className="field h-8 w-auto py-0 text-[13px]"
          disabled={source}
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) run('fontSize', event.target.value);
            event.target.value = '';
          }}
        >
          <option value="">Tamaño</option>
          {SIZES.map((size) => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
        <label
          className={cn(tool, 'flex cursor-pointer items-center gap-1')}
          title="Color del texto"
        >
          <span>Color</span>
          <input
            type="color"
            aria-label="Color del texto"
            className="size-5 cursor-pointer border-0 bg-transparent p-0"
            disabled={source}
            defaultValue="#1F497D"
            onChange={(event) => run('foreColor', event.target.value)}
          />
        </label>
        <button
          type="button"
          className={tool}
          title="Alinear a la izquierda"
          disabled={source}
          onClick={() => run('justifyLeft')}
        >
          Izquierda
        </button>
        <button
          type="button"
          className={tool}
          title="Centrar"
          disabled={source}
          onClick={() => run('justifyCenter')}
        >
          Centro
        </button>
        <button
          type="button"
          className={tool}
          disabled={source}
          onClick={() => {
            remember();
            setLinking(true);
          }}
        >
          Enlace
        </button>
        <button
          type="button"
          className={tool}
          disabled={source}
          onClick={() => imageInput.current?.click()}
        >
          Imagen
        </button>
        <button
          type="button"
          className={tool}
          title="Quitar el formato de lo elegido"
          disabled={source}
          onClick={() => run('removeFormat')}
        >
          Sin formato
        </button>
        <button type="button" className={tool} aria-pressed={source} onClick={toggleSource}>
          {source ? 'Ver la firma' : 'Ver el HTML'}
        </button>
        {toolbarExtra}
        <input
          ref={imageInput}
          type="file"
          accept="image/png,image/jpeg,image/gif"
          className="hidden"
          aria-label="Elegir una imagen"
          onChange={pickImage}
        />
      </div>
      {linking ? (
        <div className="flex flex-wrap items-end gap-2">
          <label
            className="text-ink-2 flex min-w-60 flex-1 flex-col gap-1 text-[13px]"
            htmlFor={`${id}-link`}
          >
            Dirección del enlace
            <input
              id={`${id}-link`}
              className="field"
              value={link}
              autoFocus
              onChange={(event) => setLink(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addLink();
                }
              }}
            />
          </label>
          <Button size="sm" onClick={addLink}>
            Poner el enlace
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setLinking(false)}>
            Cancelar
          </Button>
        </div>
      ) : null}
      {source ? (
        <textarea
          aria-label="HTML de la firma"
          className="field h-auto min-h-64 py-2 font-mono text-[12px]"
          value={code}
          spellCheck={false}
          onChange={(event) => setCode(event.target.value)}
        />
      ) : null}
      <div
        ref={area}
        role="textbox"
        aria-multiline="true"
        aria-label="Firma"
        contentEditable
        suppressContentEditableWarning
        className={cn(
          'border-line focus-visible:outline-accent min-h-40 overflow-x-auto rounded-md border p-3',
          MAIL_LOOK,
          source && 'hidden',
        )}
        onInput={changed}
        onBlur={remember}
        onKeyUp={remember}
        onMouseUp={remember}
        onPaste={paste}
      />
    </div>
  );
}
